/**
 * Badge — a generic small inline label with editorial styling. Used for
 * "matched" / "new place" / "needs review" tags inside the ConfirmationCard.
 *
 * Variants:
 *   neutral  hairline ink-200, label ink-600
 *   accent   hairline accent, label accent
 *   warn     hairline warn, label warn
 *   error    hairline error-muted, label error-muted
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export type BadgeVariant = 'neutral' | 'accent' | 'warn' | 'error' | 'success';

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

const BASE = cn(
  'inline-flex items-center gap-1',
  'px-2 py-0.5',
  'font-sans text-[0.6875rem] uppercase tracking-[0.12em]',
  'border rounded-full leading-tight',
);

const VARIANTS: Record<BadgeVariant, string> = {
  neutral: 'text-ink-600 border-ink-200',
  accent: 'text-accent border-accent',
  warn: 'text-warn border-warn',
  error: 'text-error-muted border-error-muted',
  success: 'text-success border-success',
};

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge({ variant = 'neutral', className, ...rest }, ref) {
    return (
      <span
        ref={ref}
        className={cn(BASE, VARIANTS[variant], className)}
        {...rest}
      />
    );
  },
);
